import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import api, { formatApiError, uploadProof } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import QrScanner from "@/components/QrScanner";
import SignaturePad from "@/components/SignaturePad";
import DriverRouteMap from "@/components/DriverRouteMap";
import { Truck, LogOut, PackageCheck, MapPin, CheckCircle2, Camera, Navigation, ExternalLink, QrCode, PenLine } from "lucide-react";
import { toast } from "sonner";

function dataUrlToFile(dataUrl, filename) {
  const [head, b64] = dataUrl.split(",");
  const mime = (head.match(/:(.*?);/) || [])[1] || "image/png";
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], filename, { type: mime });
}

export default function DriverDashboard() {
  const { user, logout } = useAuth();
  const { site } = useSite();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState({ active: [], completed: [] });
  const [stats, setStats] = useState(null);
  const [codes, setCodes] = useState({});
  const [route, setRoute] = useState(null);

  const load = useCallback(() => {
    api.get("/driver/jobs").then((r) => setJobs(r.data));
    api.get("/driver/stats").then((r) => setStats(r.data));
    api.get("/driver/route").then((r) => setRoute(r.data)).catch(() => setRoute(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const arrive = async (id) => {
    try { await api.post(`/driver/jobs/${id}/arrive`); toast.success("Marked arrived"); load(); }
    catch (e) { toast.error("Could not update"); }
  };

  const [scanCode, setScanCode] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [deliver, setDeliver] = useState(null); // { job }
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [sigData, setSigData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const runScan = async (code) => {
    if (!code || !code.trim()) return;
    try {
      const { data } = await api.post("/driver/scan", { code });
      toast.success(`#${data.order_ref} · ${data.action === "completed" ? (data.type === "delivery" ? "delivered" : "dropped at laundry") : (data.type === "delivery" ? "out for delivery" : "collected")}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const scan = () => { runScan(scanCode); setScanCode(""); };
  const onCameraScan = (text) => { setScannerOpen(false); runScan((text || "").trim()); };

  const collect = async (job) => {
    try { await api.post(`/driver/jobs/${job.id}/collect`, { code: codes[job.id] || "" }); toast.success("Marked collected"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const complete = async (job) => {
    try { await api.post(`/driver/jobs/${job.id}/complete`, {}); toast.success("Job completed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openDeliver = (job) => { setDeliver({ job }); setPhotoFile(null); setPhotoPreview(""); setSigData(null); };
  const onPhotoPick = (file) => {
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };
  const submitDelivery = async () => {
    const job = deliver.job;
    setSubmitting(true);
    try {
      let photoPath = null, sigPath = null;
      if (photoFile) photoPath = await uploadProof(photoFile);
      if (sigData) sigPath = await uploadProof(dataUrlToFile(sigData, "signature.png"));
      await api.post(`/driver/jobs/${job.id}/complete`, { photo: photoPath, signature: sigPath, origin: window.location.origin });
      toast.success(photoPath || sigPath ? "Delivered — proof saved" : "Delivered");
      setDeliver(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="driver-dashboard">
      <header className="flex items-center justify-between border-b border-white/10 px-5 py-4">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand"><Truck className="h-5 w-5" /></span>
          <div><p className="font-heading font-bold leading-none">{site?.site_name || "LaundryDiary"}</p><p className="text-xs text-slate-400">{user?.business?.name}</p></div>
        </div>
        <button onClick={() => { logout(); navigate("/"); }} className="text-slate-400 hover:text-white" data-testid="driver-logout"><LogOut className="h-5 w-5" /></button>
      </header>

      <div className="mx-auto max-w-2xl px-5 py-6">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="driver-stat-active"><p className="text-xs text-slate-400">Active jobs</p><p className="font-heading text-2xl font-bold">{stats?.active ?? 0}</p></div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="driver-stat-done"><p className="text-xs text-slate-400">Completed</p><p className="font-heading text-2xl font-bold">{stats?.completed ?? 0}</p></div>
        </div>

        <div className="mt-6 rounded-2xl border border-brand/40 bg-white/5 p-4" data-testid="scan-box">
          <p className="text-sm font-semibold text-teal-200">Scan / enter order code</p>
          <p className="text-xs text-slate-400">Scan an order QR (or type its code) to advance it — collect, out-for-delivery or delivered.</p>
          <div className="mt-3 flex gap-2">
            <Input value={scanCode} onChange={(e) => setScanCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && scan()} placeholder="e.g. A1B2C3D4" className="border-white/10 bg-white/5 uppercase text-white placeholder:text-slate-500" data-testid="scan-input" />
            <Button onClick={scan} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="scan-btn"><QrCode className="mr-1 h-4 w-4" /> Scan</Button>
          </div>
          <Button onClick={() => setScannerOpen(true)} variant="ghost" className="mt-2 w-full border border-brand/40 text-teal-200 hover:bg-white/10" data-testid="scan-camera-btn"><Camera className="mr-1 h-4 w-4" /> Scan with camera</Button>
        </div>

        <QrScanner open={scannerOpen} onOpenChange={setScannerOpen} onScan={onCameraScan} />

        <Dialog open={!!deliver} onOpenChange={(o) => !o && setDeliver(null)}>
          <DialogContent className="max-w-md" data-testid="deliver-dialog">
            <DialogHeader>
              <DialogTitle>Complete delivery · #{deliver?.job?.order_ref}</DialogTitle>
              <DialogDescription>Add a proof photo and/or a customer signature (both optional), then confirm delivery.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="mb-1.5 text-sm font-medium text-slate-700">Proof photo</p>
                {photoPreview ? (
                  <div className="relative">
                    <img src={photoPreview} alt="Proof" className="max-h-48 w-full rounded-xl object-cover" data-testid="deliver-photo-preview" />
                    <button onClick={() => { setPhotoFile(null); setPhotoPreview(""); }} className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-1 text-xs text-white">Retake</button>
                  </div>
                ) : (
                  <>
                    <input type="file" accept="image/*" capture="environment" className="hidden" id="deliver-photo-input" data-testid="deliver-photo-input" onChange={(e) => onPhotoPick(e.target.files?.[0])} />
                    <label htmlFor="deliver-photo-input" className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 py-6 text-sm text-slate-500 hover:border-brand hover:text-brand" data-testid="deliver-photo-btn"><Camera className="h-5 w-5" /> Take / choose photo</label>
                  </>
                )}
              </div>
              <div>
                <p className="mb-1.5 flex items-center gap-1 text-sm font-medium text-slate-700"><PenLine className="h-4 w-4" /> Customer signature</p>
                <SignaturePad onChange={setSigData} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeliver(null)} className="text-slate-500" data-testid="deliver-cancel">Cancel</Button>
              <Button onClick={submitDelivery} disabled={submitting} className="rounded-full bg-green-600 hover:bg-green-700" data-testid="deliver-submit"><CheckCircle2 className="mr-1 h-4 w-4" /> {submitting ? "Saving…" : "Confirm delivery"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <h2 className="mt-7 text-sm font-semibold uppercase tracking-wide text-slate-400">Today&rsquo;s route</h2>
        {route && route.stops.length > 0 ? (
          <div className="mt-3 rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="driver-route-sheet">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-300">{route.stops.length} stop{route.stops.length !== 1 ? "s" : ""} · optimised run</p>
              {route.route_url && <a href={route.route_url} target="_blank" rel="noreferrer"><Button size="sm" className="rounded-full bg-brand hover:bg-brand-hover" data-testid="driver-open-route"><Navigation className="mr-1 h-3.5 w-3.5" /> Open in Maps</Button></a>}
            </div>
            <DriverRouteMap start={route.start} stops={route.stops} />
            <div className="mt-3 space-y-2">
              {route.stops.map((s) => (
                <div key={s.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-slate-900/60 p-3" data-testid={`route-stop-${s.id}`}>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand text-sm font-bold">{s.seq}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{s.client_name}</p>
                    <p className="truncate text-xs text-slate-400"><span className="capitalize">{s.type}</span> · {s.address || "No address"}</p>
                  </div>
                  {s.arrived_at && <Badge className="border-0 bg-green-500/20 text-green-300">Arrived</Badge>}
                  <a href={s.map_link} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white" title="Map"><ExternalLink className="h-4 w-4" /></a>
                  {!s.arrived_at && <Button size="sm" variant="ghost" className="border border-white/15 text-white hover:bg-white/10" onClick={() => arrive(s.id)} data-testid={`route-arrive-${s.id}`}>Arrived</Button>}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-3 rounded-2xl border border-dashed border-white/10 p-6 text-center text-sm text-slate-500" data-testid="driver-route-empty">No stops scheduled for today.</div>
        )}

        <h2 className="mt-7 text-sm font-semibold uppercase tracking-wide text-slate-400">Your jobs</h2>
        <div className="mt-3 space-y-3" data-testid="driver-active-jobs">
          {jobs.active.map((j) => (
            <div key={j.id} className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={`driver-job-${j.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Badge className="border-0 bg-brand/20 text-teal-200 capitalize">{j.type}</Badge><span className="font-mono text-xs text-slate-400">#{j.order_ref}</span></div>
                <Badge className="border-0 bg-white/10 text-slate-200">{j.status}</Badge>
              </div>
              <p className="mt-2 font-medium">{j.client_name}</p>
              {j.address && <p className="flex items-center gap-1 text-sm text-slate-400"><MapPin className="h-3.5 w-3.5" /> {j.address}</p>}
              {(j.scheduled_date || j.scheduled_slot) && <p className="text-xs text-slate-500">{j.scheduled_date} {j.scheduled_slot}</p>}
              <div className="mt-3">
                {j.status === "assigned" && (
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Input placeholder="Confirm code (optional)" value={codes[j.id] || ""} onChange={(e) => setCodes((c) => ({ ...c, [j.id]: e.target.value }))} className="border-white/10 bg-white/5 text-white" data-testid={`driver-code-${j.id}`} />
                    <Button onClick={() => collect(j)} className="rounded-full bg-brand hover:bg-brand-hover" data-testid={`driver-collect-${j.id}`}><Camera className="mr-1 h-4 w-4" /> Mark collected</Button>
                  </div>
                )}
                {j.status === "collected" && (
                  j.type === "delivery" ? (
                    <Button onClick={() => openDeliver(j)} className="w-full rounded-full bg-green-600 hover:bg-green-700" data-testid={`driver-deliver-${j.id}`}><CheckCircle2 className="mr-1 h-4 w-4" /> Complete delivery</Button>
                  ) : (
                    <Button onClick={() => complete(j)} className="w-full rounded-full bg-green-600 hover:bg-green-700" data-testid={`driver-complete-${j.id}`}><CheckCircle2 className="mr-1 h-4 w-4" /> Complete {j.type}</Button>
                  )
                )}
              </div>
            </div>
          ))}
          {!jobs.active.length && <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-slate-500"><PackageCheck className="mx-auto h-8 w-8" /><p className="mt-2 text-sm">No jobs assigned right now.</p></div>}
        </div>

        {jobs.completed.length > 0 && (
          <>
            <h2 className="mt-7 text-sm font-semibold uppercase tracking-wide text-slate-400">Recently completed</h2>
            <div className="mt-3 space-y-2" data-testid="driver-completed-jobs">
              {jobs.completed.map((j) => (
                <div key={j.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm">
                  <span className="capitalize">{j.type} · {j.client_name}</span>
                  <span className="font-mono text-xs text-slate-400">#{j.order_ref}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
